/**
 * Defensive formatter for audit-event `summary` values.
 *
 * The `/api/audit/events` endpoint's `summary` column is a string in most
 * cases, but historic writers (notably the `care_planning` update path
 * used during the initial `ayobell-demo-v1` seed on 2026-09-06) inserted
 * raw payload objects there. Rendering a raw object as a React child
 * throws React error #31 and unmounts the entire Audit Centre page.
 *
 * This helper accepts anything and always returns a plain string,
 * suitable both for a `title` attribute and for direct JSX children.
 *
 * Handling rules (in priority order):
 *   1. null / undefined                 → ""
 *   2. string                           → returned verbatim
 *   3. number / boolean / bigint        → String(value)
 *   4. object with {section, content}   → "<section>: <content>"
 *   5. any other object                 → JSON.stringify(value) fallback
 *   6. anything else (Symbol, function) → String(value)
 *
 * Notes:
 *   - Never throws — bad JSON.stringify (circular refs) is caught and
 *     replaced with "[Unrenderable summary]".
 *   - Does NOT mutate the audit-log business data on the backend.
 *   - Does NOT change RBAC / tenant scoping.
 */
export function formatAuditSummary(value) {
  if (value == null) return "";
  const t = typeof value;
  if (t === "string") return value;
  if (t === "number" || t === "boolean" || t === "bigint") {
    return String(value);
  }
  if (t === "object") {
    // Nice-shape: {section, content, …}
    if (
      typeof value.section === "string" &&
      typeof value.content === "string"
    ) {
      return `${value.section}: ${value.content}`;
    }
    try {
      return JSON.stringify(value);
    } catch (_) {
      return "[Unrenderable summary]";
    }
  }
  // Symbol / function / anything exotic.
  try {
    return String(value);
  } catch (_) {
    return "[Unrenderable summary]";
  }
}
